import {
  Account_Committed_Truck_Capacity_Read,
  Account_Connected_Read_Nested,
  Account_Create,
  Account_Read as AccountProto,
  Account_Read_Nested,
  Account_Read_Typeahead,
  Account_Update,
  AccountDayEquipmentTypeStat_Read_Nested,
  AccountDayJobStat_Read,
  AccountType,
  Label_Read_Nested,
  PostV1CompaniesCompanyIdAccountsInviteData,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { CompanyBasic } from '~hooks/useCompany/models';
import { ContactItem } from '~hooks/useContact/models/ContactItem';
import { EquipmentTypeBasic } from '~hooks/useEquipment/models';
import { BasicLabel } from '~hooks/useLabels/models';
import { Nullable } from '~types/Nullable';
import { compareValues } from '~utils/utilFunctions';

import { BasicDepartment } from '../useDepartment/models';

export class AccountTypeahead {
  constructor(
    private _id: string,
    private _name: string,
    private _connected_company: { id: string } | null,
    private _schedule_on: boolean,
  ) {}

  public static parse(proto: Account_Read_Typeahead) {
    return new AccountTypeahead(
      proto.id,
      proto.name,
      proto.connected_company ?? null,
      proto.schedule_on,
    );
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get connectedCompanyId() {
    return this._connected_company?.id ?? null;
  }

  public get scheduleOn() {
    return this._schedule_on;
  }
}

export class AccountDayEquipmentTypeStat {
  constructor(
    private _id: string,
    private _equipment_type: EquipmentTypeBasic,
    private _committed_count: number,
    private _assigned_count: number,
  ) {}

  public static parse(proto: AccountDayEquipmentTypeStat_Read_Nested) {
    return new AccountDayEquipmentTypeStat(
      proto.id,
      EquipmentTypeBasic.parse(proto.equipment_type),
      proto.committed_count,
      proto.assigned_count,
    );
  }

  public get id() {
    return this._id;
  }

  public get equipmentType() {
    return this._equipment_type;
  }

  public get committedCount() {
    return this._committed_count;
  }

  public get assignedCount() {
    return this._assigned_count;
  }
}

export class AccountCapacityTypeahead {
  constructor(
    private _id: string,
    private _name: string,
    private _connected_company: { id: string } | null,
    private _schedule_on: boolean,
    private _current_capacity_count: number,
    private _current_assigned_count: number,
    private _labels: BasicLabel[] = [],
    private _compliant: boolean | null = null,
    private _equipment_type_stats: AccountDayEquipmentTypeStat[] = [],
  ) {}

  public static parse(proto: AccountDayJobStat_Read) {
    return new AccountCapacityTypeahead(
      proto.account.id,
      proto.account.name,
      proto.account.connected_company ?? null,
      proto.account.schedule_on,
      proto.capacity_count,
      proto.assigned_count,
      (proto.account.labels || []).map((label) => BasicLabel.parse(label)),
      proto.account.compliant ?? null,
      proto.equipment_type_stats.map((stat) => AccountDayEquipmentTypeStat.parse(stat)),
    );
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get connectedCompanyId() {
    return this._connected_company?.id ?? null;
  }

  public get scheduleOn() {
    return this._schedule_on;
  }

  public get currentCapacityCount() {
    return this._current_capacity_count;
  }

  public get currentAssignedCount() {
    return this._current_assigned_count;
  }

  public get labels() {
    return this._labels;
  }

  public get compliant() {
    return this._compliant;
  }

  public get equipmentTypeStats() {
    return this._equipment_type_stats;
  }
}

export class AccountBasic {
  public get id(): string {
    return this._id;
  }
  public get name(): string {
    return this._name;
  }
  public get accountBasicCompanyId() {
    return this._account_read_nested_company?.id;
  }
  public get billingAddress() {
    return this._billing_address;
  }

  public get connectedCompany() {
    return this._connected_company;
  }

  public get scheduleOn() {
    return Boolean(this._schedule_on);
  }

  public get billingContact() {
    return this._billing_contact;
  }

  public get primaryContact(): Nullable<ContactItem> {
    return this._account_read_nested_primary_contact;
  }

  public get accountTypes() {
    return this._account_types;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: Account_Read_Nested): AccountBasic {
    return new AccountBasic(
      proto.name ?? '',
      proto.id ?? '',
      proto.company,
      proto.billing_address ? AddressItem.parse(proto.billing_address) : null,
      proto.connected_company ? CompanyBasic.parse(proto.connected_company) : null,
      Boolean(proto.schedule_on),
      proto.billing_contact ? ContactItem.parse(proto.billing_contact) : null,
      proto.account_types ?? [],
      proto.primary_contact ? ContactItem.parse(proto.primary_contact) : null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
  constructor(
    private _name: string,
    private _id: string,
    // @syntaxbliss: needed to name this to something different from '_company' to avoid conflicts with the 'Account'
    // Class, which is wrongly mixing 'Account_Read' and 'Account_Read_Nested' types. for that same reason there is
    // No 'deparse' method in this class.
    private _account_read_nested_company?: { id: string },
    private _billing_address?: Nullable<AddressItem>,
    private _connected_company?: CompanyBasic | null,
    private _schedule_on?: boolean,
    private _billing_contact?: Nullable<ContactItem>,
    private _account_types: AccountType[] = [],
    // Named differently from the 'Account' subclass's '_primary_contact' for the
    // same reason as '_account_read_nested_company' above — TypeScript rejects a
    // private field redeclared by a subclass.
    private _account_read_nested_primary_contact: Nullable<ContactItem> = null,
    private _discarded_at: Dayjs | null = null,
  ) {}
}

export class AccountCommittedTruckCapacity {
  public get id() {
    return this._id;
  }

  public get truckCount() {
    return this._truck_count;
  }

  public get equipmentType() {
    return this._equipment_type;
  }

  public static parse(proto: Account_Committed_Truck_Capacity_Read) {
    return new AccountCommittedTruckCapacity(
      proto.id,
      proto.truck_count,
      EquipmentTypeBasic.parse(proto.equipment_type),
    );
  }

  constructor(
    private _id: string,
    private _truck_count: number,
    private _equipment_type: EquipmentTypeBasic,
  ) {}
}

class ConnectedAccountBasic {
  public get id(): string {
    return this._id;
  }

  public get accountTypes() {
    return this._account_types;
  }

  public get truckCount() {
    return this._truck_count;
  }

  public get autoAcceptOrders() {
    return this._auto_accept_orders;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public get committedTruckCapacities() {
    return this._committed_truck_capacities;
  }

  public static parse(proto: Account_Connected_Read_Nested): ConnectedAccountBasic {
    return new ConnectedAccountBasic(
      proto.id,
      proto.account_types,
      proto.truck_count,
      proto.auto_accept_orders,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.committed_truck_capacities.map(AccountCommittedTruckCapacity.parse),
    );
  }

  constructor(
    private _id: string,
    private _account_types: AccountType[],
    private _truck_count: number,
    private _auto_accept_orders: boolean,
    private _discarded_at: Dayjs | null = null,
    private _committed_truck_capacities: AccountCommittedTruckCapacity[] = [],
  ) {}
}

export class Account extends AccountBasic {
  public static parse(proto: AccountProto | any): Account {
    return new Account(
      proto,
      proto.connected_company ? CompanyBasic.parse(proto.connected_company) : null,
      proto.company ? CompanyBasic.parse(proto.company) : null,
      proto?.created_at ?? null,
      proto?.updated_at ?? null,
      proto?.address ? AddressItem.parse(proto.address) : null,
      proto?.billing_address ? AddressItem.parse(proto.billing_address) : null,
      proto.account_types?.length ? [...proto.account_types] : [],
      proto.department ? BasicDepartment.parse(proto.department) : null,
      proto?.primary_contact ? ContactItem.parse(proto.primary_contact) : null,
      proto?.billing_contact ? ContactItem.parse(proto.billing_contact) : null,
      proto?.notes ?? '',
      proto?.external_id ?? '',
      proto?.accounting_id ?? '',
      compareValues(proto.billing_address, proto.address, [
        'id',
        'place_id',
        'created_at',
        'updated_at',
      ]),
      compareValues(proto.billing_contact, proto.primary_contact, ['id']),
      proto?.auto_accept_orders || false,
      proto?.jobs_splittable || false,
      proto?.equipment_options_restricted || false,
      proto?.truck_count || null,
      proto?.managed || false,
      proto?.connected_account
        ? ConnectedAccountBasic.parse(proto.connected_account)
        : null,
      undefined,
      proto?.labels?.map((label: Label_Read_Nested) => BasicLabel.parse(label)) ?? [],
      proto?.compliant ?? null,
      proto?.show_user_phone_number ?? true,
      proto?.committed_truck_capacities?.map(AccountCommittedTruckCapacity.parse) ?? [],
      proto?.dot_number ?? '',
    );
  }

  public static deparseUpdate(proto: Account): Account_Update {
    const data = {
      // Parent_company_id: null,
      name: proto.name,
      // Company_id: proto.connectedCompany?.id || undefined,
      department_id: proto.department?.id ?? null,
      primary_contact: proto.primaryContact
        ? ContactItem.deparse(proto.primaryContact as ContactItem)
        : undefined,
      billing_contact: proto.isBillingContactSame
        ? ContactItem.deparse(proto.primaryContact as ContactItem)
        : proto.billingContact
          ? ContactItem.deparse(proto.billingContact as ContactItem)
          : undefined,
      address: AddressItem.deparse(proto.address as AddressItem),
      billing_address: proto?.isBillingAddressSame
        ? AddressItem.deparse(proto.address as AddressItem)
        : AddressItem.deparse(proto.billingAddress) || undefined,
      notes: proto.notes,
      account_types: proto.accountTypes,
      external_id: proto.externalId,
      accounting_id: proto.accountingId,
      connected_account: proto.connectedAccount
        ? {
            truck_count: proto.connectedAccount.truckCount,
          }
        : undefined,
      label_ids: proto.labels?.map((label: BasicLabel) => label.id) ?? [],
    } as any;

    // Update these fields if account is connected and it's a CUSTOMER
    if (proto.connectedCompany && proto.accountTypes.includes(AccountType.CUSTOMER)) {
      data.truck_count = proto.truckCount || 0;
      data.auto_accept_orders = proto.autoAcceptOrders;
    }

    data.jobs_splittable = proto.jobsSplittable;
    data.equipment_options_restricted = proto.equipmentOptionsRestricted;

    return data;
  }
  public static deparse(proto: Account): Account_Create {
    const data = {
      // Parent_company_id: null,
      name: proto.name,
      company_id: proto.company?.id || proto.connectedCompany?.id || undefined,
      department_id: proto.department?.id || undefined,
      primary_contact: proto.primaryContact
        ? ContactItem.deparse(proto.primaryContact as ContactItem)
        : undefined,
      billing_contact: proto.isBillingContactSame
        ? ContactItem.deparse(proto.primaryContact as ContactItem)
        : proto.billingContact
          ? ContactItem.deparse(proto.billingContact as ContactItem)
          : undefined,
      address: AddressItem.deparse(proto.address as AddressItem),
      billing_address: proto?.isBillingAddressSame
        ? AddressItem.deparse(proto.address as AddressItem)
        : AddressItem.deparse(proto.billingAddress) || undefined,
      notes: proto.notes,
      account_types: proto.accountTypes,
      external_id: proto.externalId,
      accounting_id: proto.accountingId,
      auto_accept_orders: proto.autoAcceptOrders ? proto.autoAcceptOrders : false,
      jobs_splittable: proto.jobsSplittable || false,
      equipment_options_restricted: proto.equipmentOptionsRestricted || false,
      label_ids: proto.labels?.map((label: BasicLabel) => label.id) ?? [],
    } as any;

    // ?? maybe ID is required in case of edit company
    return data as Account_Create;
  }

  public static deparseConnectedAccount(
    proto: any,
  ): PostV1CompaniesCompanyIdAccountsInviteData['body'] {
    const data = {
      name: proto.name,
      contact: proto.primaryContact
        ? ContactItem.deparse(proto.primaryContact)
        : undefined,
      address: AddressItem.deparseCreate(proto.address),

      // New fields
      company_id: proto.connectedCompany?.id || undefined,
      department_id: proto.department?.id || undefined,
      notes: proto.notes,
      account_types: proto.accountTypes,
      external_id: proto.externalId,
      accounting_id: proto.accountingId,
      auto_accept_orders: proto.autoAcceptOrders || false,
      equipment_options_restricted: proto.equipmentOptionsRestricted || false,
      truck_count: proto.truckCount === null ? 0 : proto.truckCount,
      send_invite: proto.invitesToSend ?? { email: false, sms: false },
      company_type: proto.companyType || undefined,
      time_zone: proto.timeZone || undefined,
      scoped_company_id: proto.scopedCompanyId || undefined,
      ...(proto.equipment &&
        proto.equipmentType && {
          equipment: {
            name: proto.equipment.name,
            equipment_type_name: proto.equipmentType.name,
          },
        }),
      ...(proto.internalEquipment && {
        internal_equipment: proto.internalEquipment,
      }),
      label_ids: proto.labels?.map((label: BasicLabel) => label.id) ?? [],
    };

    return data as PostV1CompaniesCompanyIdAccountsInviteData['body'];
  }

  public get createdAt(): string {
    return this._created_at;
  }
  public get updatedAt(): string {
    return this._updated_at;
  }

  public get company(): Nullable<CompanyBasic> {
    return this._company;
  }
  public get department(): Nullable<BasicDepartment> {
    return this._department;
  }

  public get address(): Nullable<AddressItem> {
    return this._address;
  }
  public get primaryContact(): Nullable<ContactItem> {
    return this._primary_contact;
  }

  public get notes(): string {
    return this._notes;
  }
  public get externalId(): string {
    return this._externalId;
  }

  public get accountingId(): string {
    return this._accountingId;
  }
  public get dotNumber() {
    return this._dot_number;
  }
  public get isBillingAddressSame(): boolean {
    return this._isBillingAddressSame;
  }
  public get isBillingContactSame(): boolean {
    return this._isBillingContactSame;
  }
  public get isCustomerAccount(): boolean {
    return this.accountTypes.includes(AccountType.CUSTOMER);
  }
  public get isVendorAccount(): boolean {
    return this.accountTypes.includes(AccountType.VENDOR);
  }
  public get autoAcceptOrders(): boolean | { value: boolean } {
    return this._auto_accept_orders || false;
  }
  public get jobsSplittable(): boolean {
    return this._jobs_splittable || false;
  }
  public get equipmentOptionsRestricted(): boolean {
    return this._equipment_options_restricted || false;
  }
  public get truckCount(): number | null {
    return this._truck_count;
  }

  public get allowToSetTruckCount(): boolean {
    return this.isCustomerAccount && (this.truckCount ?? 0) > 0;
  }

  public get connectedAccount(): Nullable<ConnectedAccountBasic> {
    return this._connected_account;
  }

  public get committedTruckCapacities() {
    return this._committed_truck_capacities;
  }

  public get managed(): boolean {
    return this._managed;
  }

  public get labels() {
    return this._labels;
  }

  public get compliant() {
    return this._compliant;
  }

  public get showUserPhoneNumber() {
    return this._show_user_phone_number;
  }

  constructor(
    proto: AccountProto,
    _connected_company: Nullable<CompanyBasic>,
    private _company: Nullable<CompanyBasic>,
    private _created_at: string,
    private _updated_at: string,
    private _address: Nullable<AddressItem>,
    _billing_address: Nullable<AddressItem>,
    _account_types: AccountType[],
    private _department: Nullable<BasicDepartment>,
    private _primary_contact: Nullable<ContactItem>,
    _billing_contact: Nullable<ContactItem>,
    private _notes: string,
    private _externalId: string,
    private _accountingId: string,
    private _isBillingAddressSame: boolean,
    private _isBillingContactSame: boolean,
    private _auto_accept_orders: boolean,
    private _jobs_splittable: boolean,
    private _equipment_options_restricted: boolean,
    private _truck_count: number | null,
    private _managed: boolean,
    private _connected_account?: Nullable<ConnectedAccountBasic>,
    _schedule_on?: boolean,
    private _labels: BasicLabel[] = [],
    private _compliant: boolean | null = null,
    private _show_user_phone_number: boolean = true,
    private _committed_truck_capacities: AccountCommittedTruckCapacity[] = [],
    private _dot_number: string = '',
  ) {
    super(
      proto.name,
      proto.id as string,
      proto.company,
      _billing_address,
      _connected_company,
      _schedule_on,
      proto.billing_contact ? ContactItem.parse(proto.billing_contact) : null,
      _account_types,
      // `Account` tracks its own `_primary_contact` field (see the getter override
      // above), so the base class's slot is left at its default here.
      undefined,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    ); //As string due to BE conditional id?
  }
}
